/** Decision cards and confirmed commands (docs/12-ux.md §4 rules 1-2): nothing that moves money happens from one
 *  click; a command shows "Sending…" until the engine applies or rejects it. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../lib/api.ts";
import { invalidate } from "../lib/data.ts";

export type CmdState = { phase: "idle" } | { phase: "sending" } | { phase: "done"; message: string } | { phase: "rejected"; message: string };

export function useCommand(botId: string) {
  const [state, setState] = useState<CmdState>({ phase: "idle" });
  const send = async (body: Record<string, unknown>) => {
    setState({ phase: "sending" });
    try {
      const { id } = await api<{ id: string }>(`/accounts/${botId}/commands`, { method: "POST", body });
      for (let k = 0; k < 90; k++) { // the engine applies state commands at once; flatten waits for the next bar close
        await new Promise((r) => setTimeout(r, k < 10 ? 500 : 2000));
        const { command } = await api<{ command: { status: string; result: { ok: boolean; message: string } | null } }>(`/accounts/${botId}/commands/${id}`);
        if (command.status === "applied") { setState({ phase: "done", message: command.result?.message ?? "Done" }); invalidate("/accounts"); invalidate("/overview"); return; }
        if (command.status === "rejected") { setState({ phase: "rejected", message: command.result?.message ?? "Rejected" }); return; }
      }
      setState({ phase: "rejected", message: "The engine has not confirmed yet. It will apply the command when it reconnects." });
    } catch (e) { setState({ phase: "rejected", message: (e as Error).message }); }
  };
  return { state, send, reset: () => setState({ phase: "idle" }) };
}

export function DecisionDialog({ open, title, what, when, tradeoff, confirm, tone = "primary", onConfirm, onClose, children }: {
  open: boolean; title: string; what: ReactNode; when: ReactNode; tradeoff: ReactNode; confirm: string; tone?: "primary" | "warn";
  onConfirm: () => void; onClose: () => void; children?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const d = ref.current; if (!d) return; if (open && !d.open) d.showModal(); if (!open && d.open) d.close(); }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} className="w-[min(520px,calc(100vw-32px))] rounded-card border border-line bg-card p-0 text-ink backdrop:bg-black/30">
      <form method="dialog" className="flex flex-col gap-3 p-6" onSubmit={(e) => { e.preventDefault(); onConfirm(); }}>
        <h2 className="h2">{title}</h2>
        <dl className="m-0 flex flex-col gap-2 text-[14px] leading-relaxed">
          <div><dt className="eyebrow">What</dt><dd className="m-0">{what}</dd></div>
          <div><dt className="eyebrow">When</dt><dd className="m-0">{when}</dd></div>
          <div><dt className="eyebrow">Trade-off</dt><dd className="m-0 rounded-[10px] bg-soft px-3 py-2">{tradeoff}</dd></div>
        </dl>
        {children}
        <div className="mt-2 flex flex-wrap justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className={tone === "warn" ? "btn-warn" : "btn-primary"}>{confirm}</button>
        </div>
      </form>
    </dialog>
  );
}

export function CmdNote({ state }: { state: CmdState }) {
  if (state.phase === "idle") return null;
  const [cls, text] = state.phase === "sending" ? ["bg-soft text-ink/80", "Sending… waiting for the engine to confirm."]
    : state.phase === "done" ? ["bg-ok-soft text-ok-ink", `Confirmed by the engine: ${state.message}`] : ["bg-warn-soft text-warn-ink", `Not applied: ${state.message}`];
  return <div role="status" className={`rounded-lg px-3 py-2 text-[14px] ${cls}`}>{text}</div>;
}
